import {
  confidenceLevels,
  mealTypes,
  nutritionSourceKinds,
  type CanonicalNutritionEntryDraft,
  type ConfidenceLevel,
  type MealType,
  type NutritionSourceKind,
} from '@calorie-tracker/nutrition';
import {
  aiSuggestionStatuses,
  auditActorTypes,
  imageUploadPurposes,
  imageUploadStatuses,
  logChatMessageRoles,
  logChatMessageStatuses,
  logChatModes,
  logChatSessionStatuses,
  type AiSuggestionStatus,
  type AuditActorType,
  type FoodSourceEvidence,
  type ImageUploadMetadata,
  type ImageUploadPurpose,
  type ImageUploadStatus,
  type LogChatMessageRole,
  type LogChatMessageStatus,
  type LogChatMode,
  type LogChatSessionStatus,
} from './contracts.ts';
import { index, integer, real, sqliteTable, text, uniqueIndex } from 'drizzle-orm/sqlite-core';

export const users = sqliteTable(
  'users',
  {
    id: text('id').primaryKey(),
    name: text('name').notNull(),
    email: text('email').notNull(),
    emailVerified: integer('email_verified', { mode: 'boolean' }).notNull(),
    image: text('image'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    emailUniqueIdx: uniqueIndex('users_email_unique').on(table.email),
  }),
);

export const sessions = sqliteTable(
  'sessions',
  {
    id: text('id').primaryKey(),
    token: text('token').notNull(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    expiresAt: integer('expires_at', { mode: 'timestamp_ms' }).notNull(),
    ipAddress: text('ip_address'),
    userAgent: text('user_agent'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    tokenUniqueIdx: uniqueIndex('sessions_token_unique').on(table.token),
    userIdx: index('sessions_user_idx').on(table.userId),
    expiresAtIdx: index('sessions_expires_at_idx').on(table.expiresAt),
  }),
);

export const accounts = sqliteTable(
  'accounts',
  {
    id: text('id').primaryKey(),
    accountId: text('account_id').notNull(),
    providerId: text('provider_id').notNull(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    accessToken: text('access_token'),
    refreshToken: text('refresh_token'),
    idToken: text('id_token'),
    accessTokenExpiresAt: integer('access_token_expires_at', { mode: 'timestamp_ms' }),
    refreshTokenExpiresAt: integer('refresh_token_expires_at', { mode: 'timestamp_ms' }),
    scope: text('scope'),
    password: text('password'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    providerAccountUniqueIdx: uniqueIndex('accounts_provider_account_unique').on(
      table.providerId,
      table.accountId,
    ),
    userIdx: index('accounts_user_idx').on(table.userId),
  }),
);

export const verifications = sqliteTable(
  'verifications',
  {
    id: text('id').primaryKey(),
    identifier: text('identifier').notNull(),
    value: text('value').notNull(),
    expiresAt: integer('expires_at', { mode: 'timestamp_ms' }).notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    identifierValueUniqueIdx: uniqueIndex('verifications_identifier_value_unique').on(
      table.identifier,
      table.value,
    ),
    expiresAtIdx: index('verifications_expires_at_idx').on(table.expiresAt),
  }),
);

export const oauthApplications = sqliteTable(
  'oauth_applications',
  {
    id: text('id').primaryKey(),
    name: text('name'),
    icon: text('icon'),
    metadata: text('metadata'),
    clientId: text('client_id'),
    clientSecret: text('client_secret'),
    redirectUrls: text('redirect_urls'),
    type: text('type'),
    disabled: integer('disabled', { mode: 'boolean' }).default(false),
    userId: text('user_id').references(() => users.id, { onDelete: 'cascade' }),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }),
  },
  (table) => ({
    clientIdUniqueIdx: uniqueIndex('oauth_applications_client_id_unique').on(table.clientId),
    userIdx: index('oauth_applications_user_idx').on(table.userId),
  }),
);

export const oauthAccessTokens = sqliteTable(
  'oauth_access_tokens',
  {
    id: text('id').primaryKey(),
    accessToken: text('access_token'),
    refreshToken: text('refresh_token'),
    accessTokenExpiresAt: integer('access_token_expires_at', { mode: 'timestamp_ms' }),
    refreshTokenExpiresAt: integer('refresh_token_expires_at', { mode: 'timestamp_ms' }),
    clientId: text('client_id').references(() => oauthApplications.clientId, {
      onDelete: 'cascade',
    }),
    userId: text('user_id').references(() => users.id, { onDelete: 'cascade' }),
    scopes: text('scopes'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }),
  },
  (table) => ({
    accessTokenUniqueIdx: uniqueIndex('oauth_access_tokens_access_token_unique').on(
      table.accessToken,
    ),
    refreshTokenUniqueIdx: uniqueIndex('oauth_access_tokens_refresh_token_unique').on(
      table.refreshToken,
    ),
    clientIdx: index('oauth_access_tokens_client_idx').on(table.clientId),
    userIdx: index('oauth_access_tokens_user_idx').on(table.userId),
  }),
);

export const oauthConsents = sqliteTable(
  'oauth_consents',
  {
    id: text('id').primaryKey(),
    clientId: text('client_id').references(() => oauthApplications.clientId, {
      onDelete: 'cascade',
    }),
    userId: text('user_id').references(() => users.id, { onDelete: 'cascade' }),
    scopes: text('scopes'),
    consentGiven: integer('consent_given', { mode: 'boolean' }),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }),
  },
  (table) => ({
    clientIdx: index('oauth_consents_client_idx').on(table.clientId),
    userIdx: index('oauth_consents_user_idx').on(table.userId),
  }),
);

export const profiles = sqliteTable(
  'profiles',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    displayName: text('display_name'),
    timezone: text('timezone').notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userUniqueIdx: uniqueIndex('profiles_user_unique').on(table.userId),
    timezoneIdx: index('profiles_timezone_idx').on(table.timezone),
  }),
);

export const dailyGoals = sqliteTable(
  'daily_goals',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    calories: real('calories').notNull(),
    proteinGrams: real('protein_grams').notNull(),
    carbsGrams: real('carbs_grams').notNull(),
    fatGrams: real('fat_grams').notNull(),
    effectiveFrom: text('effective_from').notNull(),
    effectiveTo: text('effective_to'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userEffectiveFromUniqueIdx: uniqueIndex('daily_goals_user_effective_from_unique').on(
      table.userId,
      table.effectiveFrom,
    ),
    userEffectiveFromIdx: index('daily_goals_user_effective_from_idx').on(
      table.userId,
      table.effectiveFrom,
    ),
  }),
);

export const meals = sqliteTable(
  'meals',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    mealType: text('meal_type', { enum: mealTypes }).$type<MealType>().notNull(),
    consumedOn: text('consumed_on').notNull(),
    consumedAt: integer('consumed_at', { mode: 'timestamp_ms' }).notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userConsumedOnIdx: index('meals_user_consumed_on_idx').on(table.userId, table.consumedOn),
    userConsumedAtIdx: index('meals_user_consumed_at_idx').on(table.userId, table.consumedAt),
    mealTypeIdx: index('meals_meal_type_idx').on(table.mealType),
  }),
);

export const canonicalFoods = sqliteTable(
  'canonical_foods',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    name: text('name').notNull(),
    normalizedName: text('normalized_name').notNull(),
    defaultQuantityAmount: real('default_quantity_amount').notNull(),
    defaultQuantityUnit: text('default_quantity_unit').notNull(),
    currentVersionId: text('current_version_id'),
    source: text('source', { enum: nutritionSourceKinds }).$type<NutritionSourceKind>().notNull(),
    confidence: text('confidence', { enum: confidenceLevels }).$type<ConfidenceLevel>().notNull(),
    firstLoggedAt: integer('first_logged_at', { mode: 'timestamp_ms' }).notNull(),
    lastLoggedAt: integer('last_logged_at', { mode: 'timestamp_ms' }).notNull(),
    logCount: integer('log_count').notNull(),
    archivedAt: integer('archived_at', { mode: 'timestamp_ms' }),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userNormalizedNameUniqueIdx: uniqueIndex('canonical_foods_user_normalized_name_unique').on(
      table.userId,
      table.normalizedName,
    ),
    userArchivedIdx: index('canonical_foods_user_archived_idx').on(table.userId, table.archivedAt),
    userLastLoggedIdx: index('canonical_foods_user_last_logged_idx').on(
      table.userId,
      table.lastLoggedAt,
    ),
  }),
);

export const imageUploads = sqliteTable(
  'image_uploads',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    r2ObjectKey: text('r2_object_key').notNull(),
    bucketName: text('bucket_name').notNull(),
    originalFileName: text('original_file_name').notNull(),
    contentType: text('content_type').notNull(),
    byteSize: integer('byte_size').notNull(),
    sha256: text('sha256'),
    purpose: text('purpose', { enum: imageUploadPurposes }).$type<ImageUploadPurpose>().notNull(),
    status: text('status', { enum: imageUploadStatuses }).$type<ImageUploadStatus>().notNull(),
    metadata: text('metadata', { mode: 'json' }).$type<ImageUploadMetadata | null>(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    objectKeyUniqueIdx: uniqueIndex('image_uploads_r2_object_key_unique').on(table.r2ObjectKey),
    userStatusIdx: index('image_uploads_user_status_idx').on(table.userId, table.status),
    sha256Idx: index('image_uploads_sha256_idx').on(table.sha256),
  }),
);

export const logChatSessions = sqliteTable(
  'log_chat_sessions',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    title: text('title').notNull(),
    mode: text('mode', { enum: logChatModes }).$type<LogChatMode>().notNull(),
    status: text('status', { enum: logChatSessionStatuses })
      .$type<LogChatSessionStatus>()
      .notNull(),
    activeSuggestionId: text('active_suggestion_id'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userUpdatedAtIdx: index('log_chat_sessions_user_updated_at_idx').on(
      table.userId,
      table.updatedAt,
    ),
    userStatusIdx: index('log_chat_sessions_user_status_idx').on(table.userId, table.status),
    activeSuggestionIdx: index('log_chat_sessions_active_suggestion_idx').on(
      table.activeSuggestionId,
    ),
  }),
);

export const logChatMessages = sqliteTable(
  'log_chat_messages',
  {
    id: text('id').primaryKey(),
    sessionId: text('session_id')
      .notNull()
      .references(() => logChatSessions.id, { onDelete: 'cascade' }),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    role: text('role', { enum: logChatMessageRoles }).$type<LogChatMessageRole>().notNull(),
    text: text('text').notNull(),
    status: text('status', { enum: logChatMessageStatuses })
      .$type<LogChatMessageStatus>()
      .notNull(),
    toolActivities: text('tool_activities', { mode: 'json' }).$type<
      Array<Record<string, unknown>> | null
    >(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    sessionCreatedAtIdx: index('log_chat_messages_session_created_at_idx').on(
      table.sessionId,
      table.createdAt,
    ),
    userSessionIdx: index('log_chat_messages_user_session_idx').on(table.userId, table.sessionId),
  }),
);

export const logChatMessageAttachments = sqliteTable(
  'log_chat_message_attachments',
  {
    id: text('id').primaryKey(),
    messageId: text('message_id')
      .notNull()
      .references(() => logChatMessages.id, { onDelete: 'cascade' }),
    uploadId: text('upload_id')
      .notNull()
      .references(() => imageUploads.id, { onDelete: 'cascade' }),
    kind: text('kind', { enum: ['image'] }).$type<'image'>().notNull(),
    name: text('name').notNull(),
    contentType: text('content_type').notNull(),
    size: integer('size').notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    messageIdx: index('log_chat_message_attachments_message_idx').on(table.messageId),
    uploadIdx: index('log_chat_message_attachments_upload_idx').on(table.uploadId),
  }),
);

export const foodSources = sqliteTable(
  'food_sources',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    kind: text('kind', { enum: nutritionSourceKinds }).notNull(),
    provider: text('provider').notNull(),
    providerReference: text('provider_reference'),
    confidence: text('confidence', { enum: confidenceLevels }).$type<ConfidenceLevel>().notNull(),
    imageUploadId: text('image_upload_id').references(() => imageUploads.id, {
      onDelete: 'set null',
    }),
    rawEvidence: text('raw_evidence', { mode: 'json' }).$type<FoodSourceEvidence | null>(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userKindIdx: index('food_sources_user_kind_idx').on(table.userId, table.kind),
    providerReferenceIdx: index('food_sources_provider_reference_idx').on(
      table.provider,
      table.providerReference,
    ),
    imageUploadIdx: index('food_sources_image_upload_idx').on(table.imageUploadId),
  }),
);

export const canonicalFoodVersions = sqliteTable(
  'canonical_food_versions',
  {
    id: text('id').primaryKey(),
    canonicalFoodId: text('canonical_food_id')
      .notNull()
      .references(() => canonicalFoods.id, { onDelete: 'cascade' }),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    quantityAmount: real('quantity_amount').notNull(),
    quantityUnit: text('quantity_unit').notNull(),
    canonicalQuantityAmount: real('canonical_quantity_amount').notNull(),
    canonicalQuantityUnit: text('canonical_quantity_unit').notNull(),
    calories: real('calories').notNull(),
    proteinGrams: real('protein_grams').notNull(),
    carbsGrams: real('carbs_grams').notNull(),
    fatGrams: real('fat_grams').notNull(),
    fiberGrams: real('fiber_grams'),
    sugarGrams: real('sugar_grams'),
    sodiumMilligrams: real('sodium_milligrams'),
    source: text('source', { enum: nutritionSourceKinds }).$type<NutritionSourceKind>().notNull(),
    confidence: text('confidence', { enum: confidenceLevels }).$type<ConfidenceLevel>().notNull(),
    foodSourceId: text('food_source_id').references(() => foodSources.id, {
      onDelete: 'set null',
    }),
    createdFromEntryId: text('created_from_entry_id'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    foodIdx: index('canonical_food_versions_food_idx').on(table.canonicalFoodId),
    userFoodIdx: index('canonical_food_versions_user_food_idx').on(
      table.userId,
      table.canonicalFoodId,
    ),
    entryIdx: index('canonical_food_versions_entry_idx').on(table.createdFromEntryId),
  }),
);

export const foodEntries = sqliteTable(
  'food_entries',
  {
    id: text('id').primaryKey(),
    mealId: text('meal_id')
      .notNull()
      .references(() => meals.id, { onDelete: 'cascade' }),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    foodSourceId: text('food_source_id')
      .notNull()
      .references(() => foodSources.id, { onDelete: 'restrict' }),
    canonicalFoodId: text('canonical_food_id').references(() => canonicalFoods.id, {
      onDelete: 'set null',
    }),
    canonicalFoodVersionId: text('canonical_food_version_id').references(
      () => canonicalFoodVersions.id,
      {
        onDelete: 'set null',
      },
    ),
    originalInput: text('original_input').notNull(),
    normalizedName: text('normalized_name').notNull(),
    inputQuantityAmount: real('input_quantity_amount').notNull(),
    inputQuantityUnit: text('input_quantity_unit').notNull(),
    canonicalQuantityAmount: real('canonical_quantity_amount').notNull(),
    canonicalQuantityUnit: text('canonical_quantity_unit').notNull(),
    calories: real('calories').notNull(),
    proteinGrams: real('protein_grams').notNull(),
    carbsGrams: real('carbs_grams').notNull(),
    fatGrams: real('fat_grams').notNull(),
    fiberGrams: real('fiber_grams'),
    sugarGrams: real('sugar_grams'),
    sodiumMilligrams: real('sodium_milligrams'),
    confidence: text('confidence', { enum: confidenceLevels }).$type<ConfidenceLevel>().notNull(),
    requiresReview: integer('requires_review', { mode: 'boolean' }).notNull(),
    userEdits: text('user_edits', { mode: 'json' }).$type<Record<string, unknown> | null>(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    mealIdx: index('food_entries_meal_idx').on(table.mealId),
    userIdx: index('food_entries_user_idx').on(table.userId),
    foodSourceIdx: index('food_entries_food_source_idx').on(table.foodSourceId),
    canonicalFoodIdx: index('food_entries_canonical_food_idx').on(table.canonicalFoodId),
    normalizedNameIdx: index('food_entries_normalized_name_idx').on(table.normalizedName),
  }),
);

export const recipes = sqliteTable(
  'recipes',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    name: text('name').notNull(),
    normalizedName: text('normalized_name').notNull(),
    archivedAt: integer('archived_at', { mode: 'timestamp_ms' }),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userNameUniqueIdx: uniqueIndex('recipes_user_normalized_name_unique').on(
      table.userId,
      table.normalizedName,
    ),
    userArchivedIdx: index('recipes_user_archived_idx').on(table.userId, table.archivedAt),
  }),
);

export const recipeVersions = sqliteTable(
  'recipe_versions',
  {
    id: text('id').primaryKey(),
    recipeId: text('recipe_id')
      .notNull()
      .references(() => recipes.id, { onDelete: 'cascade' }),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    versionNumber: integer('version_number').notNull(),
    totalYieldAmount: real('total_yield_amount').notNull(),
    totalYieldUnit: text('total_yield_unit').notNull(),
    calories: real('calories').notNull(),
    proteinGrams: real('protein_grams').notNull(),
    carbsGrams: real('carbs_grams').notNull(),
    fatGrams: real('fat_grams').notNull(),
    fiberGrams: real('fiber_grams'),
    sugarGrams: real('sugar_grams'),
    sodiumMilligrams: real('sodium_milligrams'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    recipeVersionUniqueIdx: uniqueIndex('recipe_versions_recipe_version_unique').on(
      table.recipeId,
      table.versionNumber,
    ),
    userRecipeIdx: index('recipe_versions_user_recipe_idx').on(table.userId, table.recipeId),
  }),
);

export const recipeIngredients = sqliteTable(
  'recipe_ingredients',
  {
    id: text('id').primaryKey(),
    recipeVersionId: text('recipe_version_id')
      .notNull()
      .references(() => recipeVersions.id, { onDelete: 'cascade' }),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    position: integer('position').notNull(),
    originalInput: text('original_input').notNull(),
    normalizedName: text('normalized_name').notNull(),
    mealType: text('meal_type', { enum: mealTypes }).$type<MealType>().notNull(),
    inputQuantityAmount: real('input_quantity_amount').notNull(),
    inputQuantityUnit: text('input_quantity_unit').notNull(),
    canonicalQuantityAmount: real('canonical_quantity_amount').notNull(),
    canonicalQuantityUnit: text('canonical_quantity_unit').notNull(),
    calories: real('calories').notNull(),
    proteinGrams: real('protein_grams').notNull(),
    carbsGrams: real('carbs_grams').notNull(),
    fatGrams: real('fat_grams').notNull(),
    fiberGrams: real('fiber_grams'),
    sugarGrams: real('sugar_grams'),
    sodiumMilligrams: real('sodium_milligrams'),
    source: text('source', { enum: nutritionSourceKinds }).notNull(),
    confidence: text('confidence', { enum: confidenceLevels }).$type<ConfidenceLevel>().notNull(),
    requiresReview: integer('requires_review', { mode: 'boolean' }).notNull(),
    consumedAt: integer('consumed_at', { mode: 'timestamp_ms' }).notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    recipeVersionIdx: index('recipe_ingredients_recipe_version_idx').on(table.recipeVersionId),
    userRecipeVersionIdx: index('recipe_ingredients_user_recipe_version_idx').on(
      table.userId,
      table.recipeVersionId,
    ),
  }),
);

export const aiSuggestions = sqliteTable(
  'ai_suggestions',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    chatSessionId: text('chat_session_id').references(() => logChatSessions.id, {
      onDelete: 'set null',
    }),
    assistantMessageId: text('assistant_message_id').references(() => logChatMessages.id, {
      onDelete: 'set null',
    }),
    mealType: text('meal_type', { enum: mealTypes }).$type<MealType>(),
    status: text('status', { enum: aiSuggestionStatuses }).$type<AiSuggestionStatus>().notNull(),
    inputText: text('input_text'),
    transcript: text('transcript'),
    imageUploadIds: text('image_upload_ids', { mode: 'json' }).$type<string[] | null>(),
    draftEntries: text('draft_entries', { mode: 'json' })
      .$type<CanonicalNutritionEntryDraft[]>()
      .notNull(),
    clarificationQuestion: text('clarification_question'),
    rawModelResponse: text('raw_model_response', { mode: 'json' }).$type<Record<
      string,
      unknown
    > | null>(),
    confirmedMealId: text('confirmed_meal_id').references(() => meals.id, {
      onDelete: 'set null',
    }),
    reviewedAt: integer('reviewed_at', { mode: 'timestamp_ms' }),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    userStatusIdx: index('ai_suggestions_user_status_idx').on(table.userId, table.status),
    chatSessionIdx: index('ai_suggestions_chat_session_idx').on(table.chatSessionId),
    assistantMessageIdx: index('ai_suggestions_assistant_message_idx').on(
      table.assistantMessageId,
    ),
    confirmedMealIdx: index('ai_suggestions_confirmed_meal_idx').on(table.confirmedMealId),
    createdAtIdx: index('ai_suggestions_created_at_idx').on(table.createdAt),
  }),
);

export const auditEvents = sqliteTable(
  'audit_events',
  {
    id: text('id').primaryKey(),
    userId: text('user_id').references(() => users.id, { onDelete: 'set null' }),
    entityType: text('entity_type').notNull(),
    entityId: text('entity_id').notNull(),
    eventType: text('event_type').notNull(),
    actorType: text('actor_type', { enum: auditActorTypes }).$type<AuditActorType>().notNull(),
    actorId: text('actor_id'),
    payload: text('payload', { mode: 'json' }).$type<Record<string, unknown> | null>(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => ({
    entityIdx: index('audit_events_entity_idx').on(table.entityType, table.entityId),
    userIdx: index('audit_events_user_idx').on(table.userId),
    createdAtIdx: index('audit_events_created_at_idx').on(table.createdAt),
  }),
);
